
import { Options, prop, Vue } from "vue-class-component";
import { PropType } from "vue";
import { createFocusTrap, FocusTrap } from "focus-trap";
import { OptionsClass } from "@/domain/entities/MainApp";
import { ClientNamePOSData } from "@/domain/entities/Partner";
import absolutePosition from "@/app/infrastructures/misc/common-library/dom/absolutePosition";

class Props {
  visible = prop<boolean>({ default: false });
  clients = prop<ClientNamePOSData[]>({ default: () => [] });
  origins = prop<OptionsClass[]>({ default: () => [] });
  selectedOrigin = prop<OptionsClass | null>({
    default: null,
    type: Object as PropType<OptionsClass | null>,
  });
  selectedServiceTypes = prop<OptionsClass[]>({ default: () => [] });
  serviceTypeOptions = prop<OptionsClass[]>({ default: () => [] });
  searchKeyword = prop<string>({ default: "" });
  isInitialLoading = prop<boolean>({ default: false });
  isLoadingMore = prop<boolean>({ default: false });
  hasNextPage = prop<boolean>({ default: false });
  listErrorMessage = prop<string>({ default: "" });
  isOriginLoading = prop<boolean>({ default: false });
  originsHasNextPage = prop<boolean>({ default: false });
  selectedClient = prop<ClientNamePOSData | null>({
    default: null,
    type: Object as PropType<ClientNamePOSData | null>,
  });
}

@Options({
  name: "DaftarKlienPopupV4",
  emits: [
    "close",
    "update:search",
    "update:origin",
    "update:service-types",
    "origin-focus",
    "origin-filter",
    "origin-scrolled",
    "load-more",
    "reset",
    "select",
    "retry",
  ],
  watch: {
    visible: {
      handler: "onVisibleChange",
    },
  },
})
export default class DaftarKlienPopupV4 extends Vue.with(Props) {
  dialogFocusTrap: FocusTrap | null = null;
  focusTrap: FocusTrap | null = null;
  isFilterOpen = false;
  highlightedClientCode: string | null = null;
  get hasActiveFilters(): boolean {
    return Boolean(
      this.selectedOrigin?.value || this.selectedServiceTypes.length,
    );
  }

  get emptyStateMessage(): string {
    if (this.searchKeyword.trim() || this.hasActiveFilters) {
      return this.$t("bookingV4.client.emptyFiltered") as string;
    }
    return this.$t("bookingV4.client.emptyInitial") as string;
  }

  get emptyStateTestId(): string {
    if (this.searchKeyword.trim()) {
      return "booking-v4-client-list-keyword-not-found";
    }
    if (this.hasActiveFilters) return "booking-v4-client-list-empty-filter";
    return "booking-v4-client-list-empty-no-data";
  }

  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.dialogFocusTrap?.deactivate();
      this.dialogFocusTrap = null;
      return;
    }
    this.$nextTick(() => {
      const dialog = this.$refs.dialog as HTMLElement;
      if (!dialog) return;
      this.dialogFocusTrap = createFocusTrap(dialog, {
        escapeDeactivates: false,
        clickOutsideDeactivates: false,
        fallbackFocus: dialog,
      });
      this.dialogFocusTrap.activate();
      const filterClientSearchEl = document.getElementById(
        "filter-client-input-search",
      );
      if (filterClientSearchEl) filterClientSearchEl.focus();
    });
  }

  get tooltipStyle() {
    return {
      fontFamily: "Montserrat",
      width: "419px",
      height: this.selectedServiceTypes.length ? "250px" : "200px",
      radius: "5px",
      border: "1px",
      borderColor: "#EAECF0",
      boxShadow: "0px 1px 2px 0px #00000014",
      backgroundColor: "#FFFFFF",
      color: "#4D4D4D",
      fontSize: "14px",
      marginTop: "3px",
    };
  }

  async openFilter(event: any) {
    const $refs: any = this.$refs;

    if ($refs.op.visible) return;
    $refs.op.show(event);
    await this.$nextTick();
    const container = document.getElementById("client-filter-op");
    if (!container) return;
    absolutePosition(container, event.currentTarget, $refs.op.visible);
    this.isFilterOpen = true;
    // if (event.pointerType) return
    await this.$nextTick();
    this.focusTrap = createFocusTrap(container, {
      escapeDeactivates: true,
      allowOutsideClick: true,
      returnFocusOnDeactivate: true,
      clickOutsideDeactivates: false,
      onDeactivate: () => {
        $refs.op.hide();
        this.focusTrap = null;
      },
    });
    this.focusTrap.activate();
  }

  onEscFilter(): void {
    if (this.focusTrap) {
      this.focusTrap.deactivate();
    }
  }

  unmounted(): void {
    this.dialogFocusTrap?.deactivate();
    this.focusTrap?.deactivate();
  }

  close(): void {
    this.$emit("close");
  }

  updateSearch(value: string): void {
    this.$emit("update:search", value || "");
  }

  updateOrigin(value: OptionsClass | null): void {
    this.$emit("update:origin", value);
  }

  onOriginFocus(): void {
    this.$emit("origin-focus");
  }

  onOriginFilter(search: string): void {
    this.$emit("origin-filter", search || "");
  }

  onOriginScrolled(search: string): void {
    this.$emit("origin-scrolled", search || "");
  }

  updateServiceTypes(value: OptionsClass[]): void {
    this.$emit("update:service-types", value || []);
  }

  removeServiceType(value: string): void {
    this.updateServiceTypes(
      this.selectedServiceTypes.filter((item) => item.value !== value),
    );
  }

  resetFilters(): void {
    this.$emit("reset");
  }

  onFocusRow(event: FocusEvent, client: ClientNamePOSData): void {
    this.highlightedClientCode = client.clientCode;
    const input = event.target as HTMLInputElement;
    input
      .closest("tr")
      ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }

  selectClient(client: ClientNamePOSData): void {
    this.$emit("select", client);
  }

  onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const nearBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 32;
    if (nearBottom && this.hasNextPage && !this.isLoadingMore) {
      this.$emit("load-more");
    }
  }
}
